import _ from "lodash";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { connect } from "react-redux";
import classNames from "classnames";

import {
  Badge,
  Button,
  ButtonDisclosure,
  Dropdown,
  DropdownMenu,
  Modal,
  SearchInput,
  SideNavigation,
} from "@toddle-edu/ds-web";
import { CaretDownFilled, FilterOutlined } from "@toddle-edu/ds-icons";

import { compose, graphql } from "lib/apolloClient";
import ACLStore from "lib/aclStore";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";
import { ELEMENT_TYPE_DP_SYLLABUS } from "Constants/stringConstants";
import { colors } from "Constants";
import { getSelectedCurriculumProgram } from "modules/CommonSelectors";
import { useI18n } from "Hooks";
import { getQuerySkipFlagsForConfiguredColumns } from "Utils";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";
import { getUserInfo } from "Login/modules/LoginModule";
import FiltersOverlay from "UIComponents/FiltersOverlay/FiltersOverlay";
import StandardsTable from "StandardsTable";
import useStandardsTableProps from "StandardsTable/hooks/useStandardsTableProps";
import { handleSelectionChange } from "StandardsTable/modules/Utils";
import {
  ASSOCIATED_PARENT_TYPES_TO_VARIABLE,
  COLUMN_TYPE_MAP_TO_SKIP_FLAGS,
} from "PlannerElementDataSet/modules/PlannerElementDataSetConstants";
import { ENABLE_STANDARDS_OUTLINE } from "Constants/permissionConstants";

import {
  getCollectiveData,
  getSideNavigationOptions,
  getFlattenedNestedNodes,
  getPlannerElementNodesQueryVariables,
  getColumnProperties,
  getColumnIdsToDisplay,
  getFilterOverlayOptions,
  getAllFiltersCount,
  getAppliedFiltersCountForFilter,
  getParentNodesCount,
  getParentNodeCount,
  getIdNodesMapMemoized,
  getFillColumn,
  getEdgesFromNodeMap as _getEdgesFromNodeMap,
  getMetadata,
  getAllowedColumnsFromConfigs,
} from "../../modules/PlannerElementDataSetUtils";
import classes from "./PlannerElementDataSetModal.scss";
import {
  getCurriculumSubjectGroupsGradesAndTagsQuery,
  getPlannerElementNodeSetQuery,
  getSubjectVariantsQuery,
} from "../../modules/PlannerElementDataSetQueries";
import { getPlannerElementSetsOfCurriculumEnhancer } from "../../modules/PlannerElementDataSetEnhancers";
import DatasetTaggingBody from "AppComponents/DatasetTaggingBody";
import {
  getShouldShowNewTaggingModal,
  isParentTaggingEnabledForType,
} from "AppComponents/DatasetTaggingBody/modules/Utils";

const testId = "PlannerElementDataSetModal";
const styles = {
  modalHeaderStyle: { marginBottom: 0 },
  sideNavigationContainerStyle: {
    height: "100%",
    flex: "0 0 auto",
  },
  modalBody: {
    padding: "0px",
    minHeight: "0",
    position: "relative",
    overflow: "hidden",
  },
};
const config = { moduleType: "UnitPlan" };

const PlannerElementDataSetModal = props => {
  const {
    onCloseModal,
    label,
    plannerElementType,
    plannerElementNodes,
    value: unitPlanFieldValue,
    resolvedValue: unitPlanResolvedValue,
    isLoading: isDataLoading,
    isData,
    plannerElementNodeSetData,
    curriculumGrades,
    genericTags,
    subjectGroups,
    handleBulkUpdate,
    preSelectedNodeId,
    isStandardsField,
    configuredColumns,
    skipFlags,
    taggedSubjectGroups,
    allowedColumns,
    isDynamicField,
  } = props;

  const { t } = useI18n();

  // useMemo is used here so that the data is not recomputed on every render only on references change
  const {
    selectedValues,
    nodes,
    rootNodes,
    parentNodes,
    associatedSubjectNodes,
    groupByType,
    extraSelectedValuesSet,
  } = useMemo(() => {
    const data = getCollectiveData({
      plannerElementType,
      plannerElementNodes,
      unitPlanResolvedValue,
      plannerElementNodeSetData,
      value: unitPlanFieldValue,
    });
    return data;
  }, [
    plannerElementType,
    plannerElementNodes,
    unitPlanResolvedValue,
    plannerElementNodeSetData,
    unitPlanFieldValue,
  ]);

  const hasMultipleSubjects = _.size(associatedSubjectNodes) > 1;

  const [selectedSubject, setSelectedSubject] = useState(
    _.first(associatedSubjectNodes)?.id
  );

  const treeMapRef = useRef({});

  const filteredParentNodes = useMemo(() => {
    if (!hasMultipleSubjects) {
      return parentNodes;
    }
    return _.filter(parentNodes, parentNode => {
      const subjects = _.get(
        parentNode,
        "dataSetNodeWrapper.properties.subjects",
        []
      );
      return _.some(subjects, subject => subject.id === selectedSubject);
    });
  }, [parentNodes, hasMultipleSubjects, selectedSubject]);

  const idNodesMap = getIdNodesMapMemoized(nodes);
  const [parentNodesCount, setParentNodesCount] = useState();

  useEffect(() => {
    setParentNodesCount(
      getParentNodesCount({
        parentNodes: filteredParentNodes,
        selectedValues,
        idNodesMap,
        plannerElementType,
      })
    );
    // added dependency of idNodesMap only as other thing will always remain same from first render
    // added dependency of filteredParentNodes as it can change based on selectedSubject
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idNodesMap, filteredParentNodes]);

  const {
    filterOptions,
    defaultFilterOptions,
    preSelectedFilterOptions,
  } = useMemo(
    () =>
      getFilterOverlayOptions({
        grades: curriculumGrades,
        tags: genericTags,
        subjectGroups,
        t,
        skipFlags,
        plannerElementType,
        selectedGrades: _.get(plannerElementNodeSetData, "grades"),
        selectedSubjectGroups: taggedSubjectGroups,
      }),
    [
      curriculumGrades,
      genericTags,
      subjectGroups,
      t,
      skipFlags,
      plannerElementType,
      plannerElementNodeSetData,
      taggedSubjectGroups,
    ]
  );

  const showFilters = !_.isEmpty(filterOptions);

  const [sideNavigationTab, setSideNavigationTab] = useState();
  const [selectedRows, setSelectedRows] = useState(selectedValues || []);
  const [isFiltersOverlayOpen, setIsFiltersOverlayOpen] = useState(false);
  const [filters, setFilters] = useState(
    showFilters
      ? {
          SEARCH: "",
          ...preSelectedFilterOptions,
        }
      : {}
  );

  const resetFilters = useCallback(() => {
    const initialFilters = showFilters
      ? {
          SEARCH: "",
          ...defaultFilterOptions,
        }
      : {};
    setFilters(initialFilters);
  }, [showFilters, defaultFilterOptions]);

  useEffect(() => {
    setSelectedRows(selectedValues || []);
  }, [selectedValues]);

  useEffect(() => {
    setSideNavigationTab(
      _.find(filteredParentNodes, { id: preSelectedNodeId })
        ? preSelectedNodeId
        : _.get(_.head(filteredParentNodes), "id", "")
    );
  }, [preSelectedNodeId, filteredParentNodes]);

  const handleFilterChange = useCallback(({ filters: newFilters }) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters,
    }));
  }, []);

  const handleFilterUpdate = (key, value) => {
    setFilters(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const showLoader = isDataLoading && !isData;

  const handleFiltersOverlayClose = useCallback(
    () => setIsFiltersOverlayOpen(false),
    []
  );

  const flatNodes = useMemo(() => {
    return getFlattenedNestedNodes({
      nodes,
      rootNodes,
      sideNavigationTab,
      groupByType,
      filters,
      isStandardsField,
    });
  }, [
    nodes,
    rootNodes,
    sideNavigationTab,
    groupByType,
    filters,
    isStandardsField,
  ]);

  const appliedFiltersCount = getAllFiltersCount({ currentFilters: filters });

  const onSelectButtonClick = async () => {
    let allNodes = nodes;
    if (showNewTaggingModal) {
      allNodes = treeMapRef.current.getAncestorTree();
    }
    handleBulkUpdate({
      selectedRows: _.toArray(selectedRows),
      nodes: allNodes,
      extraSelectedValuesSet,
    });
  };

  const getSubtitle = () => {
    if (_.size(filteredParentNodes) === 1) {
      const parentNode = _.head(filteredParentNodes);
      return _.get(parentNode, "label", "");
    }
    return "";
  };

  const shouldRenderSideNavigation =
    !_.isEmpty(filteredParentNodes) && _.size(filteredParentNodes) > 1;

  const _handleSelectionChange = _selectedRows => {
    const selectedRows = Array.from(_selectedRows);
    if (shouldRenderSideNavigation) {
      // minor optimization: This will only update the current selected tab's count
      // as on selection change it will only affect the current selected tab and other tabs'count will remain as it is
      const cnt = getParentNodeCount({
        parentNodeId: sideNavigationTab,
        selectedValues: selectedRows,
        idNodesMap,
        plannerElementType,
      });
      setParentNodesCount({
        ...parentNodesCount,
        [sideNavigationTab]: cnt,
      });
    }
    setSelectedRows(selectedRows);
  };

  const sideNavigationOptions = useMemo(() => {
    return getSideNavigationOptions({
      parentNodes: filteredParentNodes,
      parentNodesCount,
    });
  }, [filteredParentNodes, parentNodesCount]);

  const subjectSwitcherOptions = useMemo(() => {
    if (!hasMultipleSubjects) {
      return [];
    }

    return _.map(associatedSubjectNodes, subject => ({
      key: subject.id,
      value: subject.id,
      label: subject.label,
    }));
  }, [hasMultipleSubjects, associatedSubjectNodes]);

  const selectedSubjectLabel = useMemo(() => {
    if (!selectedSubject) {
      return null;
    }

    const subject = _.find(associatedSubjectNodes, { id: selectedSubject });
    return subject ? subject.label : null;
  }, [selectedSubject, associatedSubjectNodes]);

  const onSwitchSubject = ({ key }) => {
    setSelectedSubject(key);
  };

  const { columns, properties, nodeMap, sideNavData } = useStandardsTableProps({
    columns: configuredColumns,
    edges: _.map(flatNodes, edge => ({
      node: {
        ...edge,
        parentId: edge.parent,
      },
    })),
    properties: getColumnProperties({
      plannerElementType,
    }),
    cellUtils: { getMetadata },
  });

  const getEdgesFromNodeMap = useCallback(
    nodeMap => {
      return _getEdgesFromNodeMap({
        nodeMap,
        selectedStandards: new Set(selectedRows),
        isParentTaggingEnabled: isParentTaggingEnabledForType({
          plannerElementType,
        }),
      });
    },
    [selectedRows, plannerElementType]
  );

  const onCellEdit = ({ params }) => {
    handleSelectionChange({
      params,
      selectedRows,
      setSelectedRows: _handleSelectionChange,
    });
  };

  const showOutline =
    ACLStore.can(ENABLE_STANDARDS_OUTLINE) && !shouldRenderSideNavigation;

  const showNewTaggingModal = getShouldShowNewTaggingModal({ isDynamicField });

  const gridClasses = classNames(classes.gridBody, {
    [classes.outlineHiddenGridBody]: !showOutline,
  });

  const initialFilters = useMemo(() => {
    const {
      filters,
      subjectGroups = [],
      grades = [],
      //dp_syllabus subject needs to be passed
      subject,
    } = plannerElementNodeSetData;
    const nodesFilters = _.omit(filters, [
      "associatedParents",
      "isArchived",
      "subjects",
    ]);
    const { associatedParents } = filters || {};
    if (!_.isEmpty(subjectGroups)) nodesFilters.subjectGroups = subjectGroups;
    if (!_.isEmpty(grades)) nodesFilters.grades = grades;
    if (!_.isEmpty(subject)) nodesFilters.subjects = [subject?.id];
    _.forEach(associatedParents, filter => {
      const key = ASSOCIATED_PARENT_TYPES_TO_VARIABLE[filter?.type];
      if (key) {
        nodesFilters[key] = filter.ids;
      }
    });
    return nodesFilters;
  }, [plannerElementNodeSetData]);

  const dynamicFilters = useMemo(() => {
    const filters = {};
    if (hasMultipleSubjects && selectedSubject) {
      filters.subjects = [selectedSubject];
      const { selectedObjectiveIds } = _.find(associatedSubjectNodes, {
        id: selectedSubject,
      });
      if (selectedObjectiveIds) filters.parentNodes = selectedObjectiveIds;
    }
    return filters;
  }, [hasMultipleSubjects, selectedSubject, associatedSubjectNodes]);

  const parentNodesData = useMemo(
    () => ({
      parentNodes: filteredParentNodes,
      entityType: isStandardsField ? "subjectIds" : "subjects",
    }),
    [filteredParentNodes, isStandardsField]
  );

  const { plannerElementSetId } = plannerElementNodeSetData;

  const renderModalBody = () => {
    if (showLoader) return <Loading />;
    return showNewTaggingModal ? (
      <DatasetTaggingBody
        plannerElementNodeSetId={plannerElementNodeSetData?.id}
        plannerElementSetId={plannerElementSetId}
        resolvedMinimalTree={unitPlanResolvedValue}
        resolvedValue={unitPlanFieldValue}
        selectedRows={selectedRows}
        updateSelectedRows={setSelectedRows}
        plannerElementType={plannerElementType}
        initialFilters={initialFilters}
        config={config}
        showSideNavigation={true}
        label={label}
        parentNodesData={parentNodesData}
        dynamicFilters={dynamicFilters}
        allowedColumns={allowedColumns}
        treeMapRef={treeMapRef}
      />
    ) : (
      <div className={classes.bodyContainer}>
        {shouldRenderSideNavigation ? (
          <SideNavigation
            dsVersion="2.0"
            options={sideNavigationOptions}
            selectedOptionKey={sideNavigationTab}
            containerStyle={styles.sideNavigationContainerStyle}
            onClick={setSideNavigationTab}
            testId={testId}
          />
        ) : null}
        <div
          className={classNames(classes.rightContainer, {
            [classes.fullWidth]: !shouldRenderSideNavigation,
          })}
        >
          <div className={classes.dataGridWrapperContainer}>
            <StandardsTable
              properties={properties}
              nodeMap={nodeMap}
              columns={columns}
              key={sideNavigationTab}
              sideNavData={sideNavData}
              showOutline={showOutline}
            >
              <StandardsTable.Body bodyClass={gridClasses}>
                <StandardsTable.Header>
                  {/* search and filter */}
                  <div className={classes.searchAndFilterContainer}>
                    <div className={classes.searchContainer}>
                      <SearchInput
                        value={filters["SEARCH"]}
                        focusSearchBySlash
                        onChange={val => handleFilterUpdate("SEARCH", val)}
                        testId={testId}
                        placeholder={t("common:search")}
                        dsVersion="2.0"
                      />
                    </div>
                    {!_.isEmpty(filterOptions) ? (
                      <Dropdown
                        overlay={
                          <FiltersOverlay
                            options={filterOptions}
                            currentFilters={filters}
                            getAllFiltersCount={getAllFiltersCount}
                            getAppliedFiltersCountForFilter={
                              getAppliedFiltersCountForFilter
                            }
                            onApply={handleFilterChange}
                            handleClose={handleFiltersOverlayClose}
                            customContainerClass={
                              classes.filterOverlayContainer
                            }
                            defaultFilters={defaultFilterOptions}
                          />
                        }
                        visible={isFiltersOverlayOpen}
                        onVisibleChange={setIsFiltersOverlayOpen}
                      >
                        <ButtonDisclosure
                          icon={<FilterOutlined />}
                          type="outlined"
                          variant="neutral"
                          dsVersion="2.0"
                        >
                          <span className={classes.filterBtn}>
                            <span>{t("common:filters")}</span>
                            <Badge
                              value={appliedFiltersCount}
                              type="numeric"
                              variant="dark"
                              shape="circle"
                              dsVersion="2.0"
                            />
                          </span>
                        </ButtonDisclosure>
                      </Dropdown>
                    ) : null}
                  </div>
                </StandardsTable.Header>

                <StandardsTable.Grid
                  freezeColumns={2}
                  fillColumn={getFillColumn({ plannerElementType })}
                  getEdgesFromNodeMap={getEdgesFromNodeMap}
                  onCellEdit={onCellEdit}
                  cellSelectionBorderColor={colors.strokeOne}
                  emptyScreenTitle={t("common:no_results_found_on_search", {
                    label,
                  })}
                  resetFilters={resetFilters}
                />
              </StandardsTable.Body>
            </StandardsTable>
          </div>
        </div>
      </div>
    );
  };
  return (
    <Modal
      hasOverlay={true}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={false}
      onClose={onCloseModal}
      dsVersion="2.0"
    >
      <Modal.Header
        hasSeparator={true}
        isClosable={true}
        subtitle={getSubtitle()}
        style={styles.modalHeaderStyle}
      >
        <div className={classes.modalHeader}>
          <h6 className={classNames(classes.modalLabel)}>{label}</h6>
          {hasMultipleSubjects ? (
            <>
              <div className={classes.separator} />
              <Dropdown
                overlay={
                  <DropdownMenu
                    options={subjectSwitcherOptions}
                    onClick={onSwitchSubject}
                    dsVersion={"2.0"}
                  />
                }
                placement={"top"}
              >
                <Button
                  dsVersion={"2.0"}
                  variant={"neutral"}
                  type={"plain"}
                  rightIcon={<CaretDownFilled />}
                  size={"medium"}
                  onClick={event => event.stopPropagation()}
                >
                  {selectedSubjectLabel || t("common:select")}
                </Button>
              </Dropdown>
            </>
          ) : null}
        </div>
      </Modal.Header>
      <Modal.Body style={styles.modalBody}>{renderModalBody()}</Modal.Body>
      <Modal.Footer hasSeparator={true}>
        <div className={classes.modalFooter}>
          <div className="heading-6">
            {t("common:selected_label_with_count", {
              count: _.size(selectedRows),
              label,
            })}
          </div>
          <div className={classes.buttonContainer}>
            <Button
              _legacy_variant={"outlined-subtle"}
              variant="neutral"
              type="outlined"
              onClick={onCloseModal}
              testId={`${testId}-modal-cancel`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant={"progressive"}
              onClick={onSelectButtonClick}
              testId={`${testId}-modal-select`}
            >
              {t("common:select")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { plannerElementNodeSetData = {}, plannerElementType } = ownProps;

  const curriculumProgram = getSelectedCurriculumProgram(state);

  const { type: curriculumType, id: curriculumId } = curriculumProgram;
  const configs = _.get(plannerElementNodeSetData, "configs", []);
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;

  const allowedColumns = getAllowedColumnsFromConfigs({ configs });
  const configuredColumns = getColumnIdsToDisplay({
    plannerElementType,
    allowedColumns,
  });

  const skipFlags = getQuerySkipFlagsForConfiguredColumns({
    configuredColumns,
    columnTypeMapToSkipFlags: COLUMN_TYPE_MAP_TO_SKIP_FLAGS,
  });

  // shouldSkipGenericTags will be updated to false in Subject variant query for dp syllabus,
  // as we show subject variants as tags
  skipFlags.shouldSkipGenericTags ||=
    plannerElementType === ELEMENT_TYPE_DP_SYLLABUS;
  const taggedSubjectGroups = _.get(plannerElementNodeSetData, "subjectGroups");

  return {
    plannerElementNodeSetData,
    curriculumType,
    ...getPlannerElementNodesQueryVariables({
      plannerElementType,
      plannerElementNodeSetData,
      curriculumProgram,
    }),
    curriculumId,
    organizationId,
    configuredColumns,
    skipFlags,
    taggedSubjectGroups,
    allowedColumns,
  };
};

export default compose(
  connect(mapStateToProps, null),
  I18nHOC({ resource: "unitPlan" }),
  getPlannerElementSetsOfCurriculumEnhancer,
  graphql(getPlannerElementNodeSetQuery, {
    name: "getPlannerElementNodeSet",
    skip: ({ plannerElementNodeSetId, skipQuery }) =>
      !plannerElementNodeSetId || !!skipQuery,
    options: ({ plannerElementNodeFilters, plannerElementNodeSetId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: plannerElementNodeSetId,
        plannerElementNodeFilters,
      },
    }),
    props({ getPlannerElementNodeSet, ownProps: { isData, isLoading } }) {
      const data = _.get(getPlannerElementNodeSet, "node", {});

      const plannerElementNodes = _.map(
        _.get(data, "plannerElementNodeSetWrapperV2.publishedData.edges", []),
        "node"
      );

      return {
        plannerElementNodes,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getPlannerElementNodeSet["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  graphql(getCurriculumSubjectGroupsGradesAndTagsQuery, {
    name: "getCurriculumSubjectGroupsGradesAndTags",
    options: ({ curriculumId, organizationId, skipFlags }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        orgId: organizationId,
        curriculumProgramId: curriculumId,
        ...skipFlags,
        genericTagsFilter: {
          types: ["GENERIC"],
          ...getCurriculumIdsFilterForTags({
            curriculumProgramId: curriculumId,
          }),
        },
      },
    }),
    skip: ({ skipFlags, skipQuery }) =>
      _.every(_.values(skipFlags)) || !!skipQuery,
    props({
      getCurriculumSubjectGroupsGradesAndTags,
      ownProps: { isData, isLoading },
    }) {
      const data = _.get(getCurriculumSubjectGroupsGradesAndTags, "node", {});
      const grades = _.get(data, "grades", []);
      const genericTags = _.get(data, "genericTagsV2", []);
      const subjectGroups = _.get(data, "subjectGroups", []);

      return {
        curriculumGrades: grades,
        genericTags,
        subjectGroups,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            [1, 2],
            getCurriculumSubjectGroupsGradesAndTags["networkStatus"]
          ) || isLoading,
      };
    },
  }),
  graphql(getSubjectVariantsQuery, {
    name: "getSubjectVariants",
    skip: ({ plannerElementNodeSetData: { subject, type }, skipQuery }) =>
      !_.isEqual(type, ELEMENT_TYPE_DP_SYLLABUS) ||
      !_.get(subject, "id") ||
      !!skipQuery,
    options: ({ plannerElementNodeSetData }) => {
      const { subject } = plannerElementNodeSetData;
      const subjectId = _.get(subject, "id", {});
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          subjectId,
        },
      };
    },
    props({ getSubjectVariants, ownProps: { isData, isLoading, skipFlags } }) {
      const data = _.get(getSubjectVariants, "node", {});
      const subjectVariants = _.get(data, "variants", []);
      // In case of DP syllabus, we show subject variants as tags
      const genericTags = _.reduce(
        subjectVariants,
        (acc, item) => {
          if (item.level) {
            acc.push(item.level);
          }
          return acc;
        },
        []
      );

      const updatedSkipFlags = {
        ...skipFlags,
        shouldSkipGenericTags: false,
      };
      return {
        genericTags,
        skipFlags: updatedSkipFlags,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getSubjectVariants["networkStatus"]) || isLoading,
      };
    },
  })
)(PlannerElementDataSetModal);
